import * as React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronRight, LogOut, Shield, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { NAV_GROUPS } from "@/config/constants";
import { Button } from "@/shared/components/ui/button";
import { useAuth } from "@/shared/auth/useAuth";
import { useSession } from "@/shared/auth/SessionContext";
import { useFeatureFlags } from "@/shared/hooks/useFeatureFlags";
import { VeltroBrandLockup } from "@/shared/components/brand/VeltroBrandLockup";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";
import {
  Sidebar as ShadcnSidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/shared/components/ui/sidebar";

const NAV_I18N_KEYS: Record<string, string> = {
  "/":                    "nav.dashboard",
  "/locations":           "nav.locations",
  "/assets":              "nav.assets",
  "/work-orders":         "nav.workOrders",
  "/pm-schedules":        "nav.pmSchedules",
  "/checklists":          "nav.checklists",
  "/labor-tracking":      "nav.laborTracking",
  "/inventory":           "nav.inventory",
  "/vendors":             "nav.vendors",
  "/incidents":           "nav.incidents",
  "/meter-readings":      "nav.meterReadings",
  "/templates":           "nav.templates",
  "/inspections":         "nav.inspections",
  "/actions":             "nav.actions",
  "/inspection-schedules":"nav.inspectionSchedules",
  "/reports":             "nav.reports",
  "/analytics":           "nav.analytics",
  "/analytics/work-orders": "nav.analyticsWorkOrders",
  "/analytics/assets":      "nav.analyticsAssets",
  "/analytics/preventive":  "nav.analyticsPreventive",
  "/analytics/team":        "nav.analyticsTeam",
  "/analytics/safety":      "nav.analyticsSafety",
  "/users":               "nav.users",
  "/audit-log":           "nav.auditLog",
  "/custom-fields":       "nav.customFields",
  "/escalation-rules":    "nav.escalationRules",
  "/integrations":        "nav.integrations",
  "/settings":            "nav.settings",
};

export function Sidebar({ ...props }: React.ComponentProps<typeof ShadcnSidebar>) {
  const { t } = useTranslation("common");
  const { isMobile, setOpenMobile } = useSidebar();
  const { user, isSystemAdmin } = useAuth();
  const { logout } = useSession();
  const location = useLocation();
  const featureFlags = useFeatureFlags();
  const [showLogoutConfirm, setShowLogoutConfirm] = React.useState(false);

  function handleNavClick() {
    if (isMobile) setOpenMobile(false);
  }

  function isGroupActive(group: (typeof NAV_GROUPS)[number]) {
    return group.items.some((item) =>
      item.href === "/" ? location.pathname === "/" : location.pathname.startsWith(item.href),
    );
  }

  return (
    <ShadcnSidebar variant="inset" collapsible="icon" {...props}>
      <SidebarHeader>
        {isMobile && (
          <div className="flex items-center justify-end px-2 pt-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7 text-sidebar-foreground hover:bg-sidebar-accent"
              aria-label={t("nav.closeMenu")}
              onClick={() => setOpenMobile(false)}
            >
              <X className="size-4" />
            </Button>
          </div>
        )}
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <NavLink to="/" onClick={handleNavClick}>
                <VeltroBrandLockup
                  size="sm"
                  subtitle={t("nav.subtitle")}
                  framed={false}
                  className="w-full"
                />
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent role="navigation" aria-label="Main navigation">
        {NAV_GROUPS.map((group) => {
          const visibleItems = group.items.filter((item) => {
            if (!item.featureFlag) return true;
            return featureFlags?.[item.featureFlag] !== false;
          });
          if (visibleItems.length === 0) return null;
          const navItems = visibleItems.map((item) => {
            const Icon = item.icon;
            const labelKey = NAV_I18N_KEYS[item.href];
            const active = item.href === "/"
              ? location.pathname === "/"
              : item.href === "/analytics"
                ? location.pathname === "/analytics"
                : location.pathname.startsWith(item.href);
            return (
              <SidebarMenuItem key={item.href}>
                <SidebarMenuButton asChild isActive={active} tooltip={labelKey ? t(labelKey) : item.label}>
                  <NavLink to={item.href} end={item.href === "/"} onClick={handleNavClick}>
                    <Icon />
                    <span>{labelKey ? t(labelKey) : item.label}</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          });

          // Single-item groups (Overview) — no collapsing
          if (group.items.length <= 1) {
            return (
              <SidebarGroup key={group.label}>
                <SidebarGroupLabel>{group.labelKey ? t(group.labelKey) : group.label}</SidebarGroupLabel>
                <SidebarMenu>{navItems}</SidebarMenu>
              </SidebarGroup>
            );
          }

          // Multi-item groups — collapsible
          return (
            <Collapsible
              key={group.label}
              defaultOpen={isGroupActive(group)}
              className="group/collapsible"
            >
              <SidebarGroup>
                <SidebarGroupLabel asChild>
                  <CollapsibleTrigger className="flex w-full items-center">
                    {group.labelKey ? t(group.labelKey) : group.label}
                    <ChevronRight className="ml-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </CollapsibleTrigger>
                </SidebarGroupLabel>
                <CollapsibleContent>
                  <SidebarMenu>{navItems}</SidebarMenu>
                </CollapsibleContent>
              </SidebarGroup>
            </Collapsible>
          );
        })}
        {isSystemAdmin && (
          <SidebarGroup>
            <SidebarGroupLabel>{t("nav.groups.system")}</SidebarGroupLabel>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={t("nav.adminPanel")}>
                  <NavLink to="/admin/dashboard">
                    <Shield />
                    <span>{t("nav.adminPanel")}</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>
        )}
      </SidebarContent>
      {user && (
        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <div className="flex items-center gap-2 rounded-md bg-sidebar-accent/30 px-2 py-1.5">
                <div className="grid flex-1 text-left text-sm leading-tight min-w-0">
                  <span className="truncate text-xs text-muted-foreground">{user.email}</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 shrink-0"
                  onClick={() => setShowLogoutConfirm(true)}
                  aria-label="Sign out"
                >
                  <LogOut className="size-4" />
                </Button>
              </div>
            </SidebarMenuItem>
          </SidebarMenu>
          <AlertDialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("signOut.confirmTitle")}</AlertDialogTitle>
                <AlertDialogDescription>{t("signOut.confirmDescription")}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("actions.cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={logout}>{t("signOut.confirm")}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </SidebarFooter>
      )}
    </ShadcnSidebar>
  );
}
